
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function About() {
  const founders = [
    {
      name: 'Matthew Kerwick',
      role: 'Tech Guru + Co-founder',
      bio: 'With 25 years of experience building technology that drives real-world change, Matthew now focuses on harnessing AI to solve practical business challenges and empower people to perform at their best.',
      image: 'https://static.readdy.ai/image/1b9c7ad5f72a1508b639eaf4b474b2ef/31d7ac433dc3f78cbe00296fddb4f147.jpeg'
    }
  ];

  const values = [
    {
      icon: 'ri-line-chart-line',
      title: 'Results-Driven',
      description: 'Every feature we build is designed to deliver measurable improvements in sales performance.'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Customer-Centric',
      description: 'We listen to our customers and build solutions that solve their real-world challenges.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We leverage cutting-edge AI technology to create training experiences that were impossible before.'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration',
      description: 'We believe the best solutions come from working closely with our customers and partners.'
    }
  ];

  const milestones = [
    {
      year: '2022',
      title: 'Company Founded',
      description: 'Sarah and Marcus founded SellSim after recognizing the massive gap in effective sales training.'
    },
    {
      year: '2023',
      title: 'AI Framework Launch',
      description: 'Released our first AI-powered sales framework generator, helping hundreds of companies create custom strategies.'
    },
    {
      year: '2024',
      title: 'Role Play Platform',
      description: 'Launched our revolutionary AI role play system, transforming how sales teams practice and improve.'
    },
    {
      year: '2024',
      title: 'Performance Dashboard',
      description: 'Currently developing advanced analytics to help managers track and optimize team performance.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-teal-900 text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://public.readdy.ai/ai/img_res/f25fa3fa6eec8cc481a5179d8079dd26.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              We're <span className="text-teal-400">Redefining</span> Sales Training
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              Our mission is to revolutionize how sales teams learn and practice, using AI to create personalized, effective training that delivers real results.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Traditional sales training is broken. Generic programs waste time and money while failing to address the unique challenges each business faces. We believe every company deserves training that's built specifically for their industry, their customers, and their goals.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                That's why we created SellSim—to combine the power of artificial intelligence with proven sales methodologies, creating personalized training experiences that actually work.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Personalized AI-generated sales frameworks</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Custom training built for your business</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">AI-powered role play for safe practice</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl"></i>
                  <span className="text-gray-700">Measurable results and performance tracking</span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <img 
                src="https://readdy.ai/api/search-image?query=AI%20technology%20visualization%20with%20sales%20training%20elements%2C%20futuristic%20interface%20showing%20personalized%20learning%20paths%2C%20data%20analytics%2C%20and%20human-AI%20collaboration%20in%20professional%20setting&width=600&height=400&seq=mission-visual&orientation=landscape"
                alt="AI-Powered Sales Training"
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Founders
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our leadership team combines deep sales expertise with cutting-edge AI technology to create revolutionary training solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {founders.map((founder, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm text-center">
                <img 
                  src={founder.image}
                  alt={founder.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {founder.name}
                </h3>
                <p className="text-teal-600 font-medium mb-4">
                  {founder.role}
                </p>
                <p className="text-gray-600">
                  {founder.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do, from product development to customer relationships.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From a simple idea to a revolutionary platform that's transforming sales training worldwide.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{milestone.year}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-300">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why AI + Role Play is Revolutionary
            </h2>
            <p className="text-xl text-teal-100 max-w-4xl mx-auto mb-8">
              For the first time in history, we can provide unlimited, personalized practice opportunities with instant feedback. This combination of AI technology and proven learning methodologies creates training experiences that were impossible just a few years ago.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">Unlimited</div>
                <div className="text-teal-100">Practice opportunities without human trainers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">Instant</div>
                <div className="text-teal-100">Feedback and performance analysis</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">Personalized</div>
                <div className="text-teal-100">Training adapted to each individual's needs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Join the Sales Training Revolution
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Ready to experience the future of sales training? Start with your free AI framework and see the difference personalized training can make.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://app.sellsim.co.uk/free-sales-strategy"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your Free Strategy
            </a>
            <Link
              to="/contact"
              className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
